import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import MediaLibraryManager from '../Media/MediaLibraryManager';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,video/mp4,video/quicktime,video/webm';

const statusStyle = (status) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.4rem 0.65rem',
    borderRadius: '5px',
    fontSize: '0.875rem',
    background: status === 'done' ? '#eaf7ee'
        : status === 'error' ? '#fdecea'
        : '#eef4fd',
});

const galleryItemId = item => item?.fileId ?? item?.file?.id ?? item?.id;

export default function ProductGalleryMultiUploaderInner({
    productId,
    gallery = [],
    onGalleryChange,
    onError,
}) {
    const { fetch, API_URL } = useAuth();
    const [attachResults, setAttachResults] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [flash, setFlash] = useState(null);

    // Files already in the gallery render greyed-out + selected inside the library
    // modal, and clicking one toggles it back out instead of adding a duplicate.
    const selectedIds = useMemo(
        () => (Array.isArray(gallery) ? gallery : Object.values(gallery || {}))
            .map(galleryItemId)
            .filter(id => id != null),
        [gallery],
    );

    const fileLabel = file => file.title || file.filename || 'File';

    const attachFiles = async files => {
        const results = files.map(file => ({
            id: file.uuid || file.id,
            filename: file.filename,
            status: 'attaching',
            error: '',
        }));
        setAttachResults(current => [...results, ...current]);

        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${productId}/media/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ files: files.map((f) => ({ fileId: f.id, description: '' })) }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || data.message || 'Failed to add files to gallery');
            }
            const data = await res.json();
            const resultIds = new Set(results.map(result => result.id));
            setAttachResults(current => current.map(result => (
                resultIds.has(result.id) ? {...result, status: 'done'} : result
            )));
            onGalleryChange?.(data.data || []);
            setFlash({
                tone: 'success',
                message: files.length === 1
                    ? `“${fileLabel(files[0])}” was added to the gallery.`
                    : `${files.length} files were added to the gallery.`,
            });
        } catch (err) {
            const resultIds = new Set(results.map(result => result.id));
            setAttachResults(current => current.map(result => (
                resultIds.has(result.id) ? {...result, status: 'error', error: err.message} : result
            )));
            setFlash({ tone: 'error', message: err.message });
            onError?.(err.message);
        }
    };

    const detachFile = async file => {
        const items = Array.isArray(gallery) ? gallery : Object.values(gallery || {});
        const index = items.findIndex(item => String(galleryItemId(item)) === String(file.id));
        if (index < 0) return;
        const item = items[index];
        // Address the entry by its stable uuid; the index is only a fallback for an
        // entry stored before uuids existed, since it goes stale as the gallery shifts.
        const request = item.uuid
            ? [`${API_URL}/v1/seller/products/${productId}/media/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uuid: item.uuid }),
            }]
            : [
                `${API_URL}/v1/seller/products/${productId}/media/${index}/delete`,
                { method: 'POST' },
            ];
        try {
            const res = await fetch(...request);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || data.message || 'Failed to remove file from gallery');
            }
            const data = await res.json();
            onGalleryChange?.(data.data || []);
            setFlash({
                tone: 'success',
                message: `“${fileLabel(file)}” was removed from the gallery.`,
            });
        } catch (err) {
            setFlash({ tone: 'error', message: err.message });
            onError?.(err.message);
        }
    };

    const doneCount = attachResults.filter((r) => r.status === 'done').length;
    const allSettled = attachResults.length > 0 && attachResults.every((r) => r.status === 'done' || r.status === 'error');

    return (
        <>
            <p>Add images or video to the gallery. You can upload new media in the same library.</p>
            <button type="button" className="button small" onClick={() => setPickerOpen(true)}>
                Add gallery media
            </button>

            {pickerOpen && (
                <MediaLibraryManager
                    variant="modal"
                    isOpen
                    selectable
                    multiSelect
                    selectedIds={selectedIds}
                    flash={flash}
                    accept={ACCEPT}
                    title="Add product gallery media"
                    onSelect={file => attachFiles([file])}
                    onDeselect={detachFile}
                    onUpload={file => attachFiles([file])}
                    onCancel={() => setPickerOpen(false)}
                />
            )}

            {attachResults.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {allSettled && (
                        <p className="help" style={{ margin: '0 0 0.25rem' }}>
                            {doneCount} of {attachResults.length} file{attachResults.length !== 1 ? 's' : ''} added to gallery.
                        </p>
                    )}
                    {attachResults.map((r, i) => (
                        <div key={i} style={statusStyle(r.status)}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.filename}
                            </span>
                            {r.status === 'attaching' && (
                                <span className="text-muted" style={{ flexShrink: 0 }}>Adding to gallery...</span>
                            )}
                            {r.status === 'done' && (
                                <span className="success" style={{ fontWeight: 500, flexShrink: 0 }}>&#10003; Added to gallery</span>
                            )}
                            {r.status === 'error' && (
                                <span className="error" role="alert" style={{ flexShrink: 0 }}>
                                    {r.error}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
